import { typeOrmDateTime } from '@sphereon/ssi-sdk.agent-config'
import {
  type CredentialProofFormat,
  type IIssuer,
  type StatusListCredential,
  StatusListCredentialIdMode,
  StatusListDriverType,
  type StatusListIndexingDirection,
  StatusListType,
  type StatusPurpose2021,
} from '@sphereon/ssi-types'

import { BaseEntity, ChildEntity, Column, Entity, OneToMany, PrimaryColumn, TableInheritance, Unique } from 'typeorm'
import { BitstringStatusListEntryEntity } from './BitstringStatusListEntryEntity'

import { StatusListEntryEntity } from './StatusList2021EntryEntity'

@Entity('StatusList')
@Unique('UQ_correlationId', ['correlationId'])
@TableInheritance({ column: { type: 'simple-enum', name: 'type', enum: StatusListType } })
export abstract class StatusListEntity extends BaseEntity {
  @PrimaryColumn({ name: 'id', type: 'varchar' })
  id!: string

  @Column({ name: 'correlationId', type: 'varchar', nullable: false })
  correlationId!: string

  @Column({ name: 'length', type: 'integer', nullable: false, unique: false })
  length!: number

  @Column('simple-enum', {
    name: 'type',
    enum: StatusListType,
    nullable: false,
  })
  type!: StatusListType

  @Column({
    name: 'issuer',
    type: 'text',
    nullable: false,
    unique: false,
    transformer: {
      from(value: string): string | IIssuer {
        if (value?.trim()?.startsWith('{')) {
          return JSON.parse(value)
        }
        return value
      },
      to(value: string | IIssuer): string {
        if (typeof value === 'string') {
          return value
        }
        return JSON.stringify(value)
      },
    },
  })
  issuer!: string | IIssuer

  @Column('simple-enum', {
    name: 'driverType',
    enum: StatusListDriverType,
    nullable: false,
    default: StatusListDriverType.AGENT_TYPEORM,
  })
  driverType!: StatusListDriverType

  @Column('simple-enum', {
    name: 'credentialIdMode',
    enum: StatusListCredentialIdMode,
    nullable: false,
    default: StatusListCredentialIdMode.ISSUANCE,
  })
  credentialIdMode!: StatusListCredentialIdMode

  @Column({ type: 'varchar', name: 'proofFormat', enum: ['lds', 'jwt'], nullable: false, default: 'lds' })
  proofFormat!: CredentialProofFormat

  @Column({
    name: 'statusListCredential',
    type: 'text',
    nullable: true,
    unique: false,
    transformer: {
      from(value: string): StatusListCredential {
        if (value?.startsWith('ey')) {
          return value
        }
        return JSON.parse(value)
      },
      to(value: StatusListCredential): string {
        if (typeof value === 'string') {
          return value
        }
        return JSON.stringify(value)
      },
    },
  })
  statusListCredential?: StatusListCredential
}

@ChildEntity(StatusListType.StatusList2021)
export class StatusList2021Entity extends StatusListEntity {
  @Column({
    type: 'varchar',
    name: 'indexingDirection',
    enum: ['rightToLeft'],
    nullable: false,
    default: 'rightToLeft',
  })
  indexingDirection!: StatusListIndexingDirection

  @Column({ type: 'varchar', name: 'statusPurpose', nullable: false, default: 'revocation' })
  statusPurpose!: StatusPurpose2021

  @OneToMany((type) => StatusListEntryEntity, (entry) => entry.statusList)
  statusListEntries!: StatusListEntryEntity[]
}

@ChildEntity(StatusListType.OAuthStatusList)
export class OAuthStatusListEntity extends StatusListEntity {
  @Column({ type: 'integer', name: 'bitsPerStatus', nullable: false })
  bitsPerStatus!: number
  @Column({ name: 'expiresAt', nullable: true, type: typeOrmDateTime() })
  expiresAt?: Date
}

@ChildEntity(StatusListType.BitstringStatusList)
export class BitstringStatusListEntity extends StatusListEntity {
  @Column({
    type: 'varchar',
    name: 'statusPurpose',
    nullable: false,
    transformer: {
      from(value: string): string | string[] {
        if (value?.includes(',')) {
          return value.split(',').map((v) => v.trim() as string)
        }
        return value as string
      },
      to(value: string | string[]): string {
        if (Array.isArray(value)) {
          return value.join(',')
        }
        return value
      },
    },
  })
  statusPurpose!: string | string[]

  @Column({ type: 'integer', name: 'bitsPerStatus', nullable: false })
  bitsPerStatus?: number

  @Column({ name: 'validFrom', nullable: true, type: typeOrmDateTime() })
  validFrom?: Date

  @Column({ name: 'validUntil', nullable: true, type: typeOrmDateTime() })
  validUntil?: Date

  @Column({ type: 'integer', name: 'ttl', nullable: true })
  ttl?: number

  @OneToMany((type) => BitstringStatusListEntryEntity, (entry) => entry.statusList)
  statusListEntries!: BitstringStatusListEntryEntity[]
}
